import { useState, useCallback, useMemo, useEffect } from 'react';
import urls from '@/legacy/config/env';
import {
	MACHINES_OPTIONS,
	LOCATIONS_OPTIONS,
	CLIENT_OPTIONS,
	ROUTES_OPTIONS,
	USERS_OPTIONS,
} from '../../../legacy/constants/queryKeys';
import { useOptionsQuery } from '@/legacy/hooks';
import { resolveApiError } from '@/shared/api-error';
import { Option } from '@/legacy/types';
import { useTranslation } from 'react-i18next';
import { useStore } from '@/legacy/store/zustand';
import { EntityFilters, FiltersConfig, PageFilterState } from './types';
import { getAllowedKeys, pickAllowed } from './utils';
import { FilterFieldValue } from '@/shared/ui/filters/FilterField';

export type FilterOptionConfig = {
	label: string;
	type: 'select' | 'multi-select' | 'range';
	options: Option[];
};

type UsePageFiltersControllerReturn = {
	filterOptions: Record<string, FilterOptionConfig>;
	isLoading: boolean;
	error: string | null;
	selectedFilterKey: string | null;
	setSelectedFilterKey: (key: string | null) => void;
	draftValues: EntityFilters;
	setDraftValue: (key: string, value: FilterFieldValue) => void;
	appliedValues: EntityFilters;
	areFiltersApplied: boolean;
	applyFilters: () => void;
	clearDraft: () => void;
	resetDraft: () => void;
};

type UsePageFiltersControllerParams = {
	filtersConfig: FiltersConfig;
	pageKey: string;
	extraOptions?: Record<string, FilterOptionConfig>;
};

const extractEntityFilters = (pageFilter: PageFilterState | undefined): EntityFilters => {
	if (!pageFilter) return {};
	return pageFilter.entities ?? {};
};

export const usePageFiltersController = ({
	filtersConfig,
	pageKey,
	extraOptions,
}: UsePageFiltersControllerParams): UsePageFiltersControllerReturn => {
	const { t } = useTranslation();
	const { filters, setEntityFilters, clearEntityFilters, ensurePage } = useStore();

	const allowedKeys = useMemo(
		() => [...getAllowedKeys(filtersConfig), ...Object.keys(extraOptions ?? {})],
		[filtersConfig, extraOptions],
	);

	useEffect(() => {
		ensurePage(pageKey);
	}, [ensurePage, pageKey]);

	const [selectedFilterKey, setSelectedFilterKey] = useState<string | null>(null);

	// Applied values from store (extract entity filters, excluding date) - memoized to prevent unnecessary re-renders
	const appliedValues = useMemo(() => extractEntityFilters(filters[pageKey]), [filters, pageKey]);

	// Local draft state for editing (initialized from store)
	const [draftValues, setDraftValues] = useState<EntityFilters>(appliedValues);

	const {
		data: machines,
		isLoading: isMachinesLoading,
		error: machinesError,
	} = useOptionsQuery(MACHINES_OPTIONS, urls.formOptions.machines, filtersConfig.machines);

	const {
		data: locations,
		isLoading: isLocationsLoading,
		error: locationsError,
	} = useOptionsQuery(LOCATIONS_OPTIONS, urls.formOptions.locations, filtersConfig.locations);

	const {
		data: clients,
		isLoading: isClientsLoading,
		error: clientsError,
	} = useOptionsQuery(CLIENT_OPTIONS, urls.formOptions.clients, filtersConfig.clients);

	const {
		data: routes,
		isLoading: isRoutesLoading,
		error: routesError,
	} = useOptionsQuery(ROUTES_OPTIONS, urls.formOptions.routes, filtersConfig.routes);

	const {
		data: users,
		isLoading: isUsersLoading,
		error: usersError,
	} = useOptionsQuery(USERS_OPTIONS, urls.formOptions.users, filtersConfig.users);

	const isLoading =
		isMachinesLoading ||
		isLocationsLoading ||
		isClientsLoading ||
		isRoutesLoading ||
		isUsersLoading;
	const machinesErrorString = resolveApiError(machinesError);
	const locationsErrorString = resolveApiError(locationsError);
	const clientsErrorString = resolveApiError(clientsError);
	const routesErrorString = resolveApiError(routesError);
	const usersErrorString = resolveApiError(usersError);
	const error =
		machinesErrorString ||
		locationsErrorString ||
		clientsErrorString ||
		routesErrorString ||
		usersErrorString ||
		null;
	const errorMessage = error?.message.text || null;

	const setDraftValue = useCallback((key: string, value: FilterFieldValue) => {
		setDraftValues((prev) => ({
			...prev,
			[key]: value,
		}));
	}, []);

	const applyFilters = useCallback(() => {
		const sanitized = pickAllowed(draftValues, allowedKeys) as EntityFilters;
		setEntityFilters(pageKey, sanitized);
	}, [pageKey, draftValues, setEntityFilters, allowedKeys]);

	const clearDraft = useCallback(() => {
		setDraftValues({});
		clearEntityFilters(pageKey);
	}, [clearEntityFilters, pageKey]);

	const resetDraft = useCallback(() => {
		setDraftValues(appliedValues);
	}, [appliedValues]);

	const allFilterOptions: Record<string, FilterOptionConfig> = {
		machines: {
			label: t('common.machines'),
			type: 'multi-select',
			options: Array.isArray(machines) ? machines : [],
		},
		routes: {
			label: t('common.routes'),
			type: 'multi-select',
			options: Array.isArray(routes) ? routes : [],
		},
		locations: {
			label: t('common.locations'),
			type: 'multi-select',
			options: Array.isArray(locations) ? locations : [],
		},
		clients: {
			label: t('common.labels'),
			type: 'multi-select',
			options: Array.isArray(clients) ? clients : [],
		},
		users: {
			label: t('common.users'),
			type: 'multi-select',
			options: Array.isArray(users) ? users : [],
		},
	};

	const filterOptions = pickAllowed(
		{ ...allFilterOptions, ...(extraOptions ?? {}) },
		allowedKeys,
	) as Record<string, FilterOptionConfig>;

	const areFiltersApplied = useMemo(() => {
		return Object.values(appliedValues).some((value) => {
			if (Array.isArray(value)) return value.length > 0;
			if (typeof value === 'object' && value !== null) {
				return Object.values(value).some((v) => v !== undefined && v !== '');
			}
			return value !== undefined && value !== '';
		});
	}, [appliedValues]);

	return {
		filterOptions,
		isLoading,
		error: errorMessage,
		selectedFilterKey,
		setSelectedFilterKey,
		draftValues,
		setDraftValue,
		appliedValues,
		areFiltersApplied,
		applyFilters,
		clearDraft,
		resetDraft,
	};
};
